/**
 * Utility functions for managing connected digital profiles in localStorage
 *
 * Data structure: Array of objects [{ dpId: string, dpName: string, connectedAt: number }]
 *
 * This utility provides a clean API for:
 * - Storing connected profiles
 * - Checking if a profile is connected
 * - Retrieving all connected profiles
 * - Removing a profile from the list
 *
 * Uses localStorage for persistence across sessions.
 */

const STORAGE_KEY = "multiply-contractor-enquiries";

/**
 * Get all connected profiles from localStorage
 * @returns {Array<{dpId: string, dpName: string, connectedAt: number}>} Array of connected profiles
 */
export function getConnectedProfiles() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }

    const profiles = JSON.parse(stored);
    // Validate structure
    if (Array.isArray(profiles)) {
      return profiles;
    }
    return [];
  } catch (error) {
    console.error("Error reading connected profiles from localStorage:", error);
    return [];
  }
}

/**
 * Check if a profile is connected (by dpId)
 * @param {string} dpId - Digital profile ID to check
 * @returns {boolean} True if profile is connected
 */
export function isProfileConnected(dpId) {
  if (!dpId) {
    return false;
  }

  const profiles = getConnectedProfiles();
  return profiles.some((profile) => profile.dpId === dpId);
}

/**
 * Add a profile to the connected profiles list
 * If the profile already exists, it updates the connectedAt timestamp
 * @param {string} dpId - Digital profile ID
 * @param {string} dpName - Digital profile name (contractor name)
 * @returns {boolean} True if successfully added/updated
 */
export function addConnectedProfile(dpId, dpName) {
  if (!dpId || !dpName) {
    console.warn("Cannot add connected profile: dpId and dpName are required");
    return false;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    const profiles = getConnectedProfiles();

    // Check if profile already exists
    const existingIndex = profiles.findIndex(
      (profile) => profile.dpId === dpId
    );

    const profileData = {
      dpId,
      dpName,
      connectedAt: Date.now(),
    };

    if (existingIndex >= 0) {
      // Update existing profile (update timestamp)
      profiles[existingIndex] = profileData;
    } else {
      // Add new profile
      profiles.push(profileData);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
    return true;
  } catch (error) {
    console.error("Error adding connected profile to localStorage:", error);
    return false;
  }
}

/**
 * Remove a profile from the connected profiles list
 * @param {string} dpId - Digital profile ID to remove
 * @returns {boolean} True if successfully removed
 */
export function removeConnectedProfile(dpId) {
  if (!dpId) {
    return false;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    const profiles = getConnectedProfiles();
    const filteredProfiles = profiles.filter(
      (profile) => profile.dpId !== dpId
    );

    if (filteredProfiles.length === profiles.length) {
      // Profile not found
      return false;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredProfiles));
    return true;
  } catch (error) {
    console.error("Error removing connected profile from localStorage:", error);
    return false;
  }
}

/**
 * Clear all connected profiles
 * @returns {boolean} True if successfully cleared
 */
export function clearConnectedProfiles() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error(
      "Error clearing connected profiles from localStorage:",
      error
    );
    return false;
  }
}

/**
 * Get a specific connected profile by dpId
 * @param {string} dpId - Digital profile ID
 * @returns {{dpId: string, dpName: string, connectedAt: number}|null} Profile object or null if not found
 */
export function getConnectedProfile(dpId) {
  if (!dpId) {
    return null;
  }

  const profiles = getConnectedProfiles();
  return profiles.find((profile) => profile.dpId === dpId) || null;
}
